import { useMemo } from "react";
import { equityCoverage } from "../../utils/equityCoverage";
import { useAccountBalanceHistory } from "../../hooks/api/mm/useAccountBalanceHistory";
import EquityChart from "./EquityChart";
import { EquityWrapper, LoadingContainer, ErrorContainer, SummaryTable, SummaryTableCell, SummaryTableHeader } from "./style";

export function Equity() {
  // Calculate timestamp for 8 days ago in nanoseconds to ensure we have data for 7 day PnL calculation
  const eightDaysAgoNano = useMemo(() => {
    const now = Date.now();
    const eightDaysAgo = now - 8 * 24 * 60 * 60 * 1000; // 8 days in milliseconds
    return String(eightDaysAgo * 1e6); // Convert to nanoseconds
  }, []);

  const { data: rawData, isLoading, error } = useAccountBalanceHistory(eightDaysAgoNano);

  const coverage = useMemo(() => equityCoverage(rawData ?? []), [rawData]);
  const { data } = coverage;

  // Extract unique strategies and organize data by strategy
  const strategiesData = useMemo(() => {
    if (!data || data.length === 0) return {};

    const strategies = new Map<string, typeof data>();

    data.forEach((balance) => {
      const strategy = balance.Strategy;
      if (!strategies.has(strategy)) {
        strategies.set(strategy, []);
      }
      strategies.get(strategy)!.push(balance);
    });

    // Define custom strategy order
    const strategyOrder = ["perp", "options_mm", "stable_yield", "spot_counterparty"];

    // Convert to object and sort strategies by custom order
    const result: Record<string, typeof data> = {};
    const allStrategyKeys = Array.from(strategies.keys());

    // First add strategies in the defined order
    strategyOrder.forEach((key) => {
      if (strategies.has(key)) {
        result[key] = strategies.get(key)!;
      }
    });

    // Then add any remaining strategies alphabetically
    allStrategyKeys
      .filter((key) => !strategyOrder.includes(key))
      .sort()
      .forEach((key) => {
        result[key] = strategies.get(key)!;
      });

    return result;
  }, [data]);

  // Calculate PnL metrics for all strategies
  const allStrategiesMetrics = useMemo(() => {
    if (!data || data.length === 0) return [];

    const strategies = Object.keys(strategiesData);

    return strategies.map((strategy) => {
      const strategyData = strategiesData[strategy];
      if (error || coverage.unavailable.includes(strategy)) {
        return { strategy, currentBalance: null, pnl5Min: null, pnl1Hour: null, pnl24Hour: null, pnl7Day: null };
      }

      // Aggregate all balances by timestamp for this strategy
      const timestampMap = new Map<string, number>();
      strategyData.forEach((balance) => {
        const timestamp = balance.Timestamp;
        const usdBalance = parseFloat(balance.USDBalance);
        const current = timestampMap.get(timestamp) || 0;
        timestampMap.set(timestamp, current + usdBalance);
      });

      // Sort by timestamp
      const sortedEntries = Array.from(timestampMap.entries()).sort(
        ([a], [b]) => Number(a) - Number(b)
      );

      if (sortedEntries.length === 0) {
        return {
          strategy,
          currentBalance: 0,
          pnl5Min: null,
          pnl1Hour: null,
          pnl24Hour: null,
          pnl7Day: null,
        };
      }

      // Get current balance (most recent timestamp)
      const currentBalance = sortedEntries[sortedEntries.length - 1][1];
      const currentTimestamp = Number(sortedEntries[sortedEntries.length - 1][0]);

      // Helper function to get smoothed balance using a time window
      // Uses median of data points within the window to reduce impact of oscillations
      const getSmoothedBalanceAtTime = (
        millisecondsAgo: number,
        windowSizeMs: number
      ): number | null => {
        const targetTimestamp = currentTimestamp - millisecondsAgo * 1e6;
        const windowStart = targetTimestamp - (windowSizeMs / 2) * 1e6;
        const windowEnd = targetTimestamp + (windowSizeMs / 2) * 1e6;

        // Collect all balances within the window
        const balancesInWindow: number[] = [];
        for (let i = sortedEntries.length - 1; i >= 0; i -= 1) {
          const entryTimestamp = Number(sortedEntries[i][0]);
          if (entryTimestamp >= windowStart && entryTimestamp <= windowEnd) {
            balancesInWindow.push(sortedEntries[i][1]);
          }
          // Early exit if we've gone past the window
          if (entryTimestamp < windowStart) {
            break;
          }
        }

        if (balancesInWindow.length === 0) {
          return null;
        }

        // Use median for robustness against outliers, but fall back to average if only 1-2 points
        if (balancesInWindow.length <= 2) {
          const sum = balancesInWindow.reduce((acc, val) => acc + val, 0);
          return sum / balancesInWindow.length;
        }

        // Calculate median
        const sortedBalances = [...balancesInWindow].sort((a, b) => a - b);
        const mid = Math.floor(sortedBalances.length / 2);
        if (sortedBalances.length % 2 === 0) {
          return (sortedBalances[mid - 1] + sortedBalances[mid]) / 2;
        }
        return sortedBalances[mid];
      };

      // Define window sizes for each time period (in milliseconds)
      // Larger windows for longer periods to smooth out more noise
      const balance5MinAgo = getSmoothedBalanceAtTime(5 * 60 * 1000, 2 * 60 * 1000); // ±1 min window
      const balance1HourAgo = getSmoothedBalanceAtTime(60 * 60 * 1000, 10 * 60 * 1000); // ±5 min window
      const balance24HourAgo = getSmoothedBalanceAtTime(24 * 60 * 60 * 1000, 60 * 60 * 1000); // ±30 min window
      const balance7DayAgo = getSmoothedBalanceAtTime(7 * 24 * 60 * 60 * 1000, 4 * 60 * 60 * 1000); // ±2 hour window

      return {
        strategy,
        currentBalance,
        pnl5Min: balance5MinAgo !== null ? currentBalance - balance5MinAgo : null,
        pnl1Hour: balance1HourAgo !== null ? currentBalance - balance1HourAgo : null,
        pnl24Hour: balance24HourAgo !== null ? currentBalance - balance24HourAgo : null,
        pnl7Day: balance7DayAgo !== null ? currentBalance - balance7DayAgo : null,
      };
    });
  }, [data, strategiesData, coverage.unavailable, error]);

  const formatPnL = (value: number | null): string => {
    if (value === null) return "N/A";
    const sign = value >= 0 ? "+" : "";
    return `${sign}${value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatBalance = (value: number | null): string => value === null ? "Unavailable" : value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const formatStrategyName = (strategy: string): string => strategy.charAt(0).toUpperCase() + strategy.slice(1).replace(/_/g, " ");

  const getPnLColor = (value: number | null): string => {
    if (value === null) return "#999";
    return value >= 0 ? "#4caf50" : "#f44336";
  };

  if (isLoading) {
    return (
      <EquityWrapper>
        <h2>Equity - Last 7 Days</h2>
        <LoadingContainer>Loading equity data...</LoadingContainer>
      </EquityWrapper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <EquityWrapper>
        <h2>Equity - Last 7 Days</h2>
        <ErrorContainer>Equity unavailable: no complete snapshots for the expected accounts.</ErrorContainer>
      </EquityWrapper>
    );
  }

  const strategies = Object.keys(strategiesData);

  return (
    <EquityWrapper>
      <h2>Equity - Last 7 Days</h2>
      {(error || coverage.unavailable.length > 0) && (
        <ErrorContainer role="alert">Current equity and PnL unavailable: {error || `incomplete or stale accounts for ${coverage.unavailable.join(", ")}`}. Charts show complete historical snapshots only.</ErrorContainer>
      )}
      {strategies.length === 0 ? (
        <ErrorContainer>No strategies found</ErrorContainer>
      ) : (
        <>
          <SummaryTable>
            <thead>
              <tr>
                <SummaryTableHeader style={{ textAlign: "left", paddingLeft: "24px" }}>Strategy</SummaryTableHeader>
                <SummaryTableHeader>Current Balance</SummaryTableHeader>
                <SummaryTableHeader>5 Min PnL</SummaryTableHeader>
                <SummaryTableHeader>1 Hour PnL</SummaryTableHeader>
                <SummaryTableHeader>24 Hour PnL</SummaryTableHeader>
                <SummaryTableHeader>7 Day PnL</SummaryTableHeader>
              </tr>
            </thead>
            <tbody>
              {allStrategiesMetrics.map((metrics) => (
                <tr key={metrics.strategy}>
                  <SummaryTableCell style={{ fontWeight: 600, textAlign: "left", paddingLeft: "24px" }}>
                    {formatStrategyName(metrics.strategy)}
                  </SummaryTableCell>
                  <SummaryTableCell>{formatBalance(metrics.currentBalance)} USD</SummaryTableCell>
                  <SummaryTableCell style={{ color: getPnLColor(metrics.pnl5Min) }}>
                    {formatPnL(metrics.pnl5Min)} USD
                  </SummaryTableCell>
                  <SummaryTableCell style={{ color: getPnLColor(metrics.pnl1Hour) }}>
                    {formatPnL(metrics.pnl1Hour)} USD
                  </SummaryTableCell>
                  <SummaryTableCell style={{ color: getPnLColor(metrics.pnl24Hour) }}>
                    {formatPnL(metrics.pnl24Hour)} USD
                  </SummaryTableCell>
                  <SummaryTableCell style={{ color: getPnLColor(metrics.pnl7Day) }}>
                    {formatPnL(metrics.pnl7Day)} USD
                  </SummaryTableCell>
                </tr>
              ))}
            </tbody>
          </SummaryTable>
          {strategies.map((strategy) => (
            <EquityChart
              key={strategy}
              strategy={strategy}
              data={strategiesData[strategy]}
            />
          ))}
        </>
      )}
    </EquityWrapper>
  );
}
